import { Calendar, Clock, Cloud, FileText, MapPin, Trophy, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { type DisciplineSplit, type RaceResult, getRaceTypesByCategory } from '../../types/raceResults'
import { Button } from '../common/Button'
import { FormField } from '../common/FormField'
import { FormIcon } from '../common/FormIcon'
import { FormInput, FormSelect, FormTextarea } from '../common/FormInput'
import { FormLabel } from '../common/FormLabel'
import { Modal } from '../common/Modal'
import { formatSecondsToTimeHHMMSS, parseTimeToSecondsFromHHMMSS } from './testResultUtils'

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.lg};
`

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }
`

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`

const FormGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.sm} ${theme.spacing.xs};
  grid-template-columns: 40px 1fr 1fr;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.lg};
`

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.sm};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

const SectionTitle = styled.h3`
  grid-column: 1 / -1;
  font-size: ${({ theme }) => theme.typography.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: ${({ theme }) => theme.spacing.md} 0 ${({ theme }) => theme.spacing.sm} 0;
`

const TimeInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`

const UnitLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 500;
  min-width: fit-content;
`

interface RaceResultModalProps {
  athleteId: string
  isOpen: boolean
  onClose: () => void
  onSave: (raceResult: Omit<RaceResult, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  raceResult?: RaceResult
}

export const RaceResultModal: React.FC<RaceResultModalProps> = ({
  athleteId,
  isOpen,
  onClose,
  onSave,
  onDelete,
  raceResult,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    location: '',
    raceType: 'sprint',
    status: 'upcoming' as RaceResult['status'],
    totalTime: '',
    overallRank: '',
    totalParticipants: '',
    overallPercentile: '',
    ageGroup: '',
    ageGroupRank: '',
    ageGroupParticipants: '',
    ageGroupPercentile: '',
    weather: '',
    temperature: '',
    notes: '',
    raceUrl: '',
  })

  const [splits, setSplits] = useState<DisciplineSplit[]>([])

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Initialize form data when modal opens or raceResult changes
  useEffect(() => {
    if (isOpen) {
      if (raceResult) {
        // Editing existing race result
        setFormData({
          name: raceResult.name,
          date: raceResult.date.split('T')[0], // Convert to YYYY-MM-DD format
          location: raceResult.location ?? '',
          raceType: raceResult.raceType,
          status: raceResult.status,
          totalTime: raceResult.totalTime ? formatSecondsToTimeHHMMSS(raceResult.totalTime) : '',
          overallRank: raceResult.overallRank?.toString() || '',
          totalParticipants: raceResult.totalParticipants?.toString() || '',
          overallPercentile: raceResult.overallPercentile?.toString() || '',
          ageGroup: raceResult.ageGroup || '',
          ageGroupRank: raceResult.ageGroupRank?.toString() || '',
          ageGroupParticipants: raceResult.ageGroupParticipants?.toString() || '',
          ageGroupPercentile: raceResult.ageGroupPercentile?.toString() || '',
          weather: raceResult.weather || '',
          temperature: raceResult.temperature?.toString() || '',
          notes: raceResult.notes || '',
          raceUrl: raceResult.raceUrl || '',
        })
        setSplits(raceResult.splits || [])
      } else {
        // Adding new race result
        setFormData({
          name: '',
          date: new Date().toISOString().split('T')[0], // Today's date
          location: '',
          raceType: 'sprint',
          status: 'upcoming',
          totalTime: '',
          overallRank: '',
          totalParticipants: '',
          overallPercentile: '',
          ageGroup: '',
          ageGroupRank: '',
          ageGroupParticipants: '',
          ageGroupPercentile: '',
          weather: '',
          temperature: '',
          notes: '',
          raceUrl: '',
        })
        setSplits([])
      }
      setErrors({})
    }
  }, [isOpen, raceResult])

  // Helper function to calculate percentile based on rank and total participants
  const calculatePercentile = (rank: number, totalParticipants: number): number => {
    if (!rank || !totalParticipants || rank <= 0 || totalParticipants <= 0) {
      return 0
    }

    // Percentile formula: ((totalParticipants - rank + 1) / totalParticipants) * 100
    // This gives percentage of participants you finished ahead of
    const percentile = Math.round(((totalParticipants - rank + 1) / totalParticipants) * 100)
    return Math.max(1, Math.min(100, percentile)) // Ensure percentile is between 1-100
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    const newFormData = { ...formData, [field]: value }
    setFormData(newFormData)

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }

    // Auto-calculate overall percentile when overall rank or total participants change
    if (field === 'overallRank' || field === 'totalParticipants') {
      const rank = Number.parseInt(field === 'overallRank' ? value : newFormData.overallRank) || 0
      const total = Number.parseInt(field === 'totalParticipants' ? value : newFormData.totalParticipants) || 0

      if (rank > 0 && total > 0) {
        const percentile = calculatePercentile(rank, total)
        setFormData((prev) => ({ ...prev, overallPercentile: percentile.toString() }))
      }
    }

    // Auto-calculate age group percentile when age group rank or participants change
    if (field === 'ageGroupRank' || field === 'ageGroupParticipants') {
      const rank = Number.parseInt(field === 'ageGroupRank' ? value : newFormData.ageGroupRank) || 0
      const total = Number.parseInt(field === 'ageGroupParticipants' ? value : newFormData.ageGroupParticipants) || 0

      if (rank > 0 && total > 0) {
        const percentile = calculatePercentile(rank, total)
        setFormData((prev) => ({ ...prev, ageGroupPercentile: percentile.toString() }))
      }
    }

    // Auto-set status to completed if date is in the past
    if (field === 'date') {
      const selectedDate = new Date(value)
      const today = new Date()
      today.setHours(0, 0, 0, 0) // Reset time to start of day for comparison

      if (selectedDate < today) {
        setFormData((prev) => ({ ...prev, status: 'completed' }))
      } else {
        setFormData((prev) => ({ ...prev, status: 'upcoming' }))
      }
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) newErrors.name = 'Race name is required'
    if (!formData.date) newErrors.date = 'Date is required'
    // Location is now optional for upcoming races

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    const raceData: Omit<RaceResult, 'id' | 'createdAt' | 'updatedAt'> = {
      userId: athleteId,
      name: formData.name.trim(),
      date: new Date(formData.date).toISOString(),
      location: formData.location.trim() || undefined,
      raceType: formData.raceType as RaceResult['raceType'],
      status: formData.status,
      totalTime: formData.totalTime ? parseTimeToSecondsFromHHMMSS(formData.totalTime) || undefined : undefined,
      overallRank: formData.overallRank ? Number.parseInt(formData.overallRank) || undefined : undefined,
      totalParticipants: formData.totalParticipants
        ? Number.parseInt(formData.totalParticipants) || undefined
        : undefined,
      overallPercentile: formData.overallPercentile
        ? Number.parseInt(formData.overallPercentile) || undefined
        : undefined,
      ageGroup: formData.ageGroup.trim() || undefined,
      ageGroupRank: formData.ageGroupRank ? Number.parseInt(formData.ageGroupRank) || undefined : undefined,
      ageGroupParticipants: formData.ageGroupParticipants
        ? Number.parseInt(formData.ageGroupParticipants) || undefined
        : undefined,
      ageGroupPercentile: formData.ageGroupPercentile
        ? Number.parseInt(formData.ageGroupPercentile) || undefined
        : undefined,
      splits: splits,
      weather: formData.weather.trim() || undefined,
      temperature: formData.temperature ? Number.parseFloat(formData.temperature) : undefined,
      notes: formData.notes.trim() || undefined,
      raceUrl: formData.raceUrl.trim() || undefined,
    }

    await onSave(raceData)
    onClose()
  }

  const handleDelete = async () => {
    if (raceResult && onDelete) {
      await onDelete(raceResult.id)
      onClose()
    }
  }

  if (!isOpen) return null

  const modalHeader = (
    <ModalHeader>
      <ModalTitle>{raceResult ? 'Edit Race Result' : 'Add Race Result'}</ModalTitle>
      <CloseButton onClick={onClose}>
        <X size={18} />
      </CloseButton>
    </ModalHeader>
  )

  const modalFooter = (
    <ModalFooter>
      <div style={{ display: 'flex', gap: '8px', marginRight: 'auto' }}>
        {raceResult && onDelete && (
          <Button variant="danger" onClick={handleDelete}>
            Delete Race
          </Button>
        )}
      </div>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSave}>
        Save
      </Button>
    </ModalFooter>
  )

  const triathlonRaces = getRaceTypesByCategory('triathlon')
  const runningRaces = getRaceTypesByCategory('running')
  const cyclingRaces = getRaceTypesByCategory('cycling')
  const swimmingRaces = getRaceTypesByCategory('swimming')
  const otherRaces = getRaceTypesByCategory('other')

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="900px" header={modalHeader} footer={modalFooter}>
      <ModalBody>
        <FormGrid>
          {/* Basic Information */}
          <FormField>
            <FormIcon>
              <Trophy size={16} />
            </FormIcon>
          </FormField>
          <FormField>
            <FormLabel>Race Name *</FormLabel>
            <FormInput
              type="text"
              variant="borderless"
              placeholder="Race name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </FormField>
          <FormField>
            <FormLabel>Race Type</FormLabel>
            <FormSelect
              variant="borderless"
              value={formData.raceType}
              onChange={(e) => handleInputChange('raceType', e.target.value)}
            >
              <optgroup label="Triathlon">
                {triathlonRaces.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Running">
                {runningRaces.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Cycling">
                {cyclingRaces.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Swimming">
                {swimmingRaces.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </optgroup>
              {otherRaces.length > 0 && (
                <optgroup label="Other">
                  {otherRaces.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </FormSelect>
          </FormField>

          <FormField>
            <FormIcon>
              <Calendar size={16} />
            </FormIcon>
          </FormField>
          <FormField>
            <FormLabel>Date *</FormLabel>
            <FormInput
              type="date"
              variant="borderless"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
            />
            {errors.date && <ErrorMessage>{errors.date}</ErrorMessage>}
          </FormField>
          <FormField>
            <FormLabel>Status</FormLabel>
            <FormSelect
              variant="borderless"
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
            >
              <option value="upcoming">Upcoming</option>
              <option value="completed">Completed</option>
              <option value="dnf">Did Not Finish (DNF)</option>
              <option value="dns">Did Not Start (DNS)</option>
            </FormSelect>
          </FormField>

          <FormField>
            <FormIcon>
              <MapPin size={16} />
            </FormIcon>
          </FormField>
          <FormField fullWidth={true}>
            <FormLabel>Location</FormLabel>
            <FormInput
              type="text"
              variant="borderless"
              placeholder="Race location"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
            />
            {errors.location && <ErrorMessage>{errors.location}</ErrorMessage>}
          </FormField>

          {/* Race Times Section */}
          {formData.status === 'completed' && (
            <>
              <SectionTitle>Race Times</SectionTitle>

              <FormField>
                <FormIcon>
                  <Clock size={16} />
                </FormIcon>
              </FormField>
              <FormField>
                <FormLabel>Total Time</FormLabel>
                <TimeInputContainer>
                  <FormInput
                    type="text"
                    variant="borderless"
                    placeholder="1:30:45"
                    value={formData.totalTime}
                    onChange={(e) => handleInputChange('totalTime', e.target.value)}
                  />
                  <UnitLabel>HH:MM:SS</UnitLabel>
                </TimeInputContainer>
              </FormField>
              <FormField />

              {/* Discipline Splits */}
              {splits.map((split, index) => (
                <React.Fragment key={split.discipline + index.toString()}>
                  <FormField />
                  <FormField>
                    <FormLabel>{split.discipline.charAt(0).toUpperCase() + split.discipline.slice(1)} Time</FormLabel>
                    <TimeInputContainer>
                      <FormInput
                        type="text"
                        variant="borderless"
                        placeholder="25:30"
                        value={formatSecondsToTimeHHMMSS(split.time)}
                        onChange={(e) => {
                          const newSplits = [...splits]
                          const timeInSeconds = parseTimeToSecondsFromHHMMSS(e.target.value) || 0
                          newSplits[index] = { ...split, time: timeInSeconds }
                          setSplits(newSplits)
                        }}
                      />
                      <UnitLabel>HH:MM:SS</UnitLabel>
                    </TimeInputContainer>
                  </FormField>
                  <FormField>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        const newSplits = splits.filter((_, i) => i !== index)
                        setSplits(newSplits)
                      }}
                    >
                      Remove
                    </Button>
                  </FormField>
                </React.Fragment>
              ))}

              {/* Add Split Button */}
              <FormField />
              <FormField>
                <FormSelect
                  variant="borderless"
                  onChange={(e) => {
                    if (e.target.value) {
                      setSplits([...splits, { discipline: e.target.value as DisciplineSplit['discipline'], time: 0 }])
                      e.target.value = '' // Reset select
                    }
                  }}
                >
                  <option value="">Add Split...</option>
                  <option value="swimming">Swim</option>
                  <option value="cycling">Cycling</option>
                  <option value="running">Running</option>
                  <option value="transition">Transition</option>
                </FormSelect>
              </FormField>
              <FormField />

              {/* Rankings Section */}
              <SectionTitle>Rankings</SectionTitle>

              <FormField />
              <FormField>
                <FormLabel>Overall Rank</FormLabel>
                <FormInput
                  type="number"
                  variant="borderless"
                  placeholder="0"
                  value={formData.overallRank}
                  onChange={(e) => handleInputChange('overallRank', e.target.value)}
                />
              </FormField>
              <FormField>
                <FormLabel>Total Participants</FormLabel>
                <FormInput
                  type="number"
                  variant="borderless"
                  placeholder="0"
                  value={formData.totalParticipants}
                  onChange={(e) => handleInputChange('totalParticipants', e.target.value)}
                />
              </FormField>

              <FormField />
              <FormField>
                <FormLabel>Age Group</FormLabel>
                <FormInput
                  type="text"
                  variant="borderless"
                  placeholder="M35-39"
                  value={formData.ageGroup}
                  onChange={(e) => handleInputChange('ageGroup', e.target.value)}
                />
              </FormField>
              <FormField>
                <FormLabel>Age Group Rank</FormLabel>
                <FormInput
                  type="number"
                  variant="borderless"
                  placeholder="0"
                  value={formData.ageGroupRank}
                  onChange={(e) => handleInputChange('ageGroupRank', e.target.value)}
                />
              </FormField>

              <FormField />
              <FormField>
                <FormLabel>Age Group Participants</FormLabel>
                <FormInput
                  type="number"
                  variant="borderless"
                  placeholder="0"
                  value={formData.ageGroupParticipants}
                  onChange={(e) => handleInputChange('ageGroupParticipants', e.target.value)}
                />
              </FormField>
              <FormField />

              {/* Show calculated percentiles */}
              <FormField />
              <FormField>
                <FormLabel>Overall Percentile</FormLabel>
                <TimeInputContainer>
                  <FormInput
                    type="text"
                    variant="borderless"
                    value={formData.overallPercentile ? `${formData.overallPercentile}%` : ''}
                    readOnly
                    disabled
                  />
                  <UnitLabel>calculated</UnitLabel>
                </TimeInputContainer>
              </FormField>
              <FormField>
                <FormLabel>Age Group Percentile</FormLabel>
                <TimeInputContainer>
                  <FormInput
                    type="text"
                    variant="borderless"
                    value={formData.ageGroupPercentile ? `${formData.ageGroupPercentile}%` : ''}
                    readOnly
                    disabled
                  />
                  <UnitLabel>calculated</UnitLabel>
                </TimeInputContainer>
              </FormField>
            </>
          )}

          {/* Additional Fields */}
          <FormField />
          <FormField>
            <FormLabel>Temperature</FormLabel>
            <TimeInputContainer>
              <FormInput
                type="number"
                variant="borderless"
                placeholder="20"
                value={formData.temperature}
                onChange={(e) => handleInputChange('temperature', e.target.value)}
              />
              <UnitLabel>°C</UnitLabel>
            </TimeInputContainer>
          </FormField>
          <FormField>
            <FormLabel>Race URL</FormLabel>
            <FormInput
              type="url"
              variant="borderless"
              placeholder="https://..."
              value={formData.raceUrl}
              onChange={(e) => handleInputChange('raceUrl', e.target.value)}
            />
          </FormField>

          {/* Weather and Notes */}
          <FormField>
            <FormIcon>
              <Cloud size={16} />
            </FormIcon>
          </FormField>
          <FormField fullWidth={true}>
            <FormLabel>Weather</FormLabel>
            <FormTextarea
              variant="borderless"
              placeholder="Weather conditions during the race..."
              value={formData.weather}
              onChange={(e) => handleInputChange('weather', e.target.value)}
            />
          </FormField>

          <FormField>
            <FormIcon>
              <FileText size={16} />
            </FormIcon>
          </FormField>
          <FormField fullWidth={true}>
            <FormLabel>Notes</FormLabel>
            <FormTextarea
              variant="borderless"
              placeholder="Additional notes about the race..."
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
            />
          </FormField>
        </FormGrid>
      </ModalBody>
    </Modal>
  )
}
